import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { TimeType } from '@repo/toolkit/types'
import { useEffect, useMemo, useRef, useState } from 'react'

import EllipsisIcon from '../../icons/ellipsis'
import Typography, { typography } from './typography'

export interface TimeInputProps {
  /**
   * Text label to display above the input
   * */
  label?: string

  /**
   * Value of the input
   * */
  value?: TimeType

  /**
   * disabled state
   * */
  disabled?: boolean

  /**
   * readOnly state
   * */
  readOnly?: boolean

  /**
   * Class name for the wrapper
   * */
  wrapperClassName?: string

  /**
   * Error message to display
   * */
  errorMessage?: string

  /**
   * Required state
   * */
  required?: boolean

  /**
   * Callback to handle the input change
   * */
  onChange?: (value: string) => void
}

const TimeInput: React.FC<TimeInputProps> = ({
  label,
  value,
  readOnly,
  disabled,
  wrapperClassName,
  errorMessage,
  required,
  onChange
}) => {
  const [time, setTime] = useState<string | undefined>(value)

  const hoursRef = useRef<HTMLInputElement>(null)
  const minutesRef = useRef<HTMLInputElement>(null)

  // Digits typed since the field got focus. Typing always fills the field from scratch,
  // so the typed digits never mix with the value already shown or depend on the caret.
  const hoursDigitsRef = useRef('')
  const minutesDigitsRef = useRef('')

  const _wrapperClassName = useMemo(
    () =>
      cn(
        'bg-white-active border border-grey-light rounded py-2.5 px-8 flex justify-center overflow-hidden focus-within:border-primary text-grey-lighter',
        wrapperClassName,
        {
          'focus-within:border-red': errorMessage,
          'border-red': errorMessage
        }
      ),
    [wrapperClassName, errorMessage]
  )

  const handleHoursChange = (hours: string) => {
    hoursDigitsRef.current = ''

    if (hours.length >= 2) {
      hours = hours.slice(-2)

      if (Number(hours) > 23) hours = '23'
    }

    setTime(`${hours}:${time?.split(':')[1] ?? '00'}`)

    if (hours.length === 2) {
      minutesRef.current?.focus()
    }
  }

  const handleMinutesChange = (minutes: string) => {
    minutesDigitsRef.current = ''

    if (minutes.length >= 2) {
      minutes = minutes.slice(-2)

      if (Number(minutes) > 59) minutes = '59'
    }

    setTime(`${time?.split(':')[0] ?? '00'}:${minutes}`)
  }

  const handleHoursBlur = (hours: string) => {
    if (hours.length === 1) setTime(`0${hours}:${time?.split(':')[1] ?? '00'}`)
  }

  const handleMinutesBlur = (minutes: string) => {
    if (minutes.length === 1) setTime(`${time?.split(':')[0] ?? '00'}:0${minutes}`)
  }

  const handleHoursDigit = (digit: string) => {
    const hours = `${hoursDigitsRef.current}${digit}`

    // A first digit above 2 cannot start a valid hour, so the hour is complete.
    if (hours.length === 2 || Number(hours) > 2) {
      hoursDigitsRef.current = ''
      setTime(
        `${String(Math.min(Number(hours), 23)).padStart(2, '0')}:${time?.split(':')[1] || '00'}`
      )
      minutesRef.current?.focus()
    } else {
      hoursDigitsRef.current = hours
      setTime(`${hours.padStart(2, '0')}:${time?.split(':')[1] || '00'}`)
    }
  }

  const handleMinutesDigit = (digit: string) => {
    const minutes = `${minutesDigitsRef.current}${digit}`

    // A first digit above 5 cannot start valid minutes, so the minutes are complete.
    minutesDigitsRef.current = minutes.length === 2 || Number(minutes) > 5 ? '' : minutes
    setTime(
      `${time?.split(':')[0] || '00'}:${String(Math.min(Number(minutes), 59)).padStart(2, '0')}`
    )
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    digitsRef: React.MutableRefObject<string>,
    onDigit: (digit: string) => void
  ) => {
    if (e.key === 'e' || e.key === '-') e.preventDefault()

    if (e.key === 'Backspace' || e.key === 'Delete') digitsRef.current = ''

    if (readOnly || e.ctrlKey || e.metaKey || e.altKey || !/^\d$/.test(e.key)) return

    e.preventDefault()
    onDigit(e.key)
  }

  useEffect(() => {
    if (time) {
      const [hours, minutes] = time.split(':')
      const formattedMinutes = minutes == '0' || minutes === '' ? '00' : minutes
      const formattedHours = hours == '0' || hours === '' ? '00' : hours

      onChange?.(`${formattedHours}:${formattedMinutes}`)
    }
  }, [time])

  useEffect(() => {
    setTime(value)
  }, [value])

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label>
          <Typography variant="label-semibold" color={tailwindColors.grey.lighter}>
            {StringFormatter.isRequired(label, required)}
          </Typography>
        </label>
      )}

      <div>
        <div className="flex items-center space-x-2">
          <div className={_wrapperClassName}>
            <input
              ref={hoursRef}
              className={cn('outline-none bg-white-active flex-1 text-center', {
                'cursor-not-allowed': disabled
              })}
              type="number"
              placeholder="HH"
              min="0"
              max="23"
              style={typography.text}
              value={time?.split(':')[0]}
              onChange={(e) => handleHoursChange(e.target.value)}
              onFocus={() => (hoursDigitsRef.current = '')}
              onBlur={(e) => handleHoursBlur(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, hoursDigitsRef, handleHoursDigit)}
              readOnly={readOnly}
              disabled={disabled}
            />
          </div>
          <EllipsisIcon color={tailwindColors.grey.stroke} width={'18px'} height={'18px'} />
          <div className={_wrapperClassName}>
            <input
              ref={minutesRef}
              className={cn('outline-none bg-white-active flex-1 text-center', {
                'cursor-not-allowed': disabled
              })}
              type="number"
              placeholder="MM"
              min="0"
              max="59"
              value={time?.split(':')[1]}
              style={typography.text}
              onChange={(e) => handleMinutesChange(e.target.value)}
              onFocus={() => (minutesDigitsRef.current = '')}
              onBlur={(e) => handleMinutesBlur(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && !e.currentTarget.value) hoursRef.current?.focus()

                handleKeyDown(e, minutesDigitsRef, handleMinutesDigit)
              }}
              readOnly={readOnly}
              disabled={disabled}
            />
          </div>
        </div>
        {errorMessage && (
          <div>
            <div className="h-1" />
            <Typography variant="small" color={tailwindColors.red.DEFAULT}>
              {errorMessage}
            </Typography>
          </div>
        )}
      </div>
    </div>
  )
}

export default TimeInput
